import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { endpoints } from "../services/apis";
import { makeUnauthenticatedPOSTRequest } from "../services/serverHelper";
import { useDispatch } from "react-redux";
import {
  setAccessToken,
  setRole,
  setRefreshToken,
} from "../reducer/slices/authSlice";

import logo from "../../assets/ek-logo.png";
import partnerBg from "../../assets/partner-bg.png"; // Make sure to add a background image for partners
import Footer from "../../components/Footer";
import "./Partnerslogin.css"; // Imported the separate CSS file

// Google Login imports
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

export function PartnerLoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [passwordVisible, setPasswordVisible] = useState(false);

  // Forgot password states
  const [showForgotModal, setShowForgotModal] = useState(false);
  const [forgotMobile, setForgotMobile] = useState("");
  const [forgotOtp, setForgotOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [forgotStageSent, setForgotStageSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);

  // Verify if this endpoint is shared or different for Partners
  const FORGOT_BASE = "https://api.ekalakaar.com/api/v1/auth/forgot-password";

  useEffect(() => {
    const prefillEmail = localStorage.getItem("prefill_email");
    if (prefillEmail) {
      setFormData((prev) => ({
        ...prev,
        email: prefillEmail || "",
      }));
      localStorage.removeItem("prefill_email");
    }
  }, []);

  function changeHandler(event) {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  }

  const togglePasswordVisibility = () => setPasswordVisible(!passwordVisible);

  // Email/password login handler
  const submitHandler = async (event) => {
    event.preventDefault();
    if (!formData.email || !formData.password) {
      return toast.error("Please enter both email and password");
    }

    const toastId = toast.loading("Logging in...");
    try {
      const response = await makeUnauthenticatedPOSTRequest(
        endpoints.LOGIN_API,
        {
          email: formData.email,
          password: formData.password,
          registerBy: "email",
        }
      );
      toast.dismiss(toastId);

      if (!response || response.error || response.status !== "success") {
        toast.error(
          response?.message ||
            response?.error ||
            "Login failed. Please check your credentials and try again."
        );
        return;
      }

      const { accessToken, refreshToken, role } = response.data || {};

      if (!accessToken || !refreshToken) {
        toast.error("Login failed. Try again.");
        return;
      }

      // --- CRITICAL CHECK: Ensure this matches your Backend Role String ---
      if (role !== "Partner") {
        toast.error("Only Partners/Artists can login here");
        return;
      }

      dispatch(setAccessToken(accessToken));
      dispatch(setRefreshToken(refreshToken));
      dispatch(setRole(role));

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("role", role);

      toast.success("Login successful!");
      navigate("/Partner_dashboard"); // Navigate to Partner Dashboard
    } catch (error) {
      toast.dismiss(toastId);
      console.error("Login error:", error);
      toast.error("Error logging in. Please try again.");
    }
  };

  // Google Sign-In handler
  const handleGoogleLoginSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);
      console.log("Google User Info:", decoded);

      const toastId = toast.loading("Logging in with Google...");
      const response = await makeUnauthenticatedPOSTRequest(
        endpoints.LOGIN_WITH_EMAIL,
        {
          email: decoded.email,
          googleIdToken: credentialResponse.credential,
          registerBy: "google",
        }
      );
      toast.dismiss(toastId);

      if (!response || response.error || response.status !== "success") {
        toast.error(
          response?.message ||
            response?.error ||
            "Google login failed. Please try again."
        );
        return;
      }

      const { accessToken, refreshToken, role } = response.data || {};

      if (!accessToken || !refreshToken) {
        toast.error("Google login failed. Try again.");
        return;
      }

      if (role !== "Partner") {
        toast.error("Only Partners/Artists can login here");
        return;
      }

      dispatch(setAccessToken(accessToken));
      dispatch(setRefreshToken(refreshToken));
      dispatch(setRole(role));

      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("role", role);

      toast.success("Google login successful!");
      navigate("/Partner_Profile");
    } catch (error) {
      console.error("Google login error:", error);
      toast.error("Google login failed");
    }
  };

  const handleGoogleLoginError = () => {
    toast.error("Google login failed");
  };

  // Forgot password handlers
  const handleForgotSendOtp = async () => {
    if (!forgotMobile) return toast.error("Please enter your mobile number");

    const toastId = toast.loading("Sending OTP...");
    try {
      const res = await fetch(`${FORGOT_BASE}/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          mobileNu: forgotMobile,
          registerBy: "mobile",
        }),
      });
      const data = await res.json();
      toast.dismiss(toastId);
      if (res.status === 200) {
        toast.success("OTP sent successfully to your mobile!");
        setForgotStageSent(true);
      } else {
        toast.error(data.message || "Failed to send OTP");
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Error sending OTP: " + err.message);
    }
  };

  const handleVerifyForgotOtp = async () => {
    if (!forgotOtp) return toast.error("Please enter OTP");

    const toastId = toast.loading("Verifying OTP...");
    try {
      const res = await fetch(`${FORGOT_BASE}/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ otp: forgotOtp }),
        credentials: "include",
      });

      const data = await res.json();
      toast.dismiss(toastId);

      if (res.status === 200) {
        toast.success("OTP verified! Now set your new password.");
        setOtpVerified(true);
        localStorage.setItem("tempAccessToken", data.data.accessToken);
      } else {
        toast.error(data.message || "Invalid OTP");
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Error verifying OTP: " + err.message);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword) return toast.error("Please enter your new password");

    const toastId = toast.loading("Updating password...");
    try {
      const res = await fetch(`${FORGOT_BASE}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("tempAccessToken")}`,
        },
        body: JSON.stringify({
          password: newPassword,
          passwordConfirm: newPassword,
        }),
      });

      const data = await res.json();
      toast.dismiss(toastId);

      if (res.status === 200) {
        toast.success("Password updated successfully!");
        localStorage.removeItem("tempAccessToken");
        setShowForgotModal(false);
        setForgotStageSent(false);
        setOtpVerified(false);
        setForgotOtp("");
        setNewPassword("");
        setForgotMobile("");
        setTimeout(() => navigate("/Partnerlogin"), 1500);
      } else {
        toast.error(data.message || "Failed to reset password");
      }
    } catch (err) {
      toast.dismiss(toastId);
      toast.error("Error updating password: " + err.message);
    }
  };

  // ---------------------- UI ------------------------

  return (
    <>
      <div className="partner-login-wrapper">
        {/* Background */}
        <div className="partner-login-background">
          <img
            src={partnerBg}
            alt="background"
            className="partner-bg-image"
          />
          <div className="partner-bg-overlay"></div>
        </div>

        {/* Login Card */}
        <div className="partner-login-content">
          <div className="partner-login-card">
            <div className="partner-login-header">
              <img src={logo} alt="logo" className="partner-logo" />
              <h2 className="partner-login-title">
                Sign in to Partner Portal
              </h2>
            </div>

            <form onSubmit={submitHandler} className="partner-login-form">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={changeHandler}
                placeholder="Enter your email"
                className="partner-input"
              />

              <div className="partner-password-wrapper">
                <input
                  type={passwordVisible ? "text" : "password"}
                  name="password"
                  required
                  onChange={changeHandler}
                  value={formData.password}
                  className="partner-input"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="partner-password-toggle"
                >
                  <i
                    className={`fas ${
                      passwordVisible ? "fa-eye-slash" : "fa-eye"
                    }`}
                  ></i>
                </button>
              </div>

              <p
                onClick={() => setShowForgotModal(true)}
                className="partner-forgot-link"
              >
                Forgot password?
              </p>

              <button
                type="submit"
                className="partner-submit-btn"
              >
                Sign In
              </button>
            </form>

            {/* Google Sign-In Button */}
            <div className="partner-google-login">
              <GoogleLogin
                onSuccess={handleGoogleLoginSuccess}
                onError={handleGoogleLoginError}
              />
            </div>

            <p className="partner-signup-text">
              Don't have an account?{" "}
              <span
                onClick={() => navigate("/partners-register")}
                className="partner-signup-link"
              >
                Sign up
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* Forgot Password Modal */}
      {showForgotModal && (
        <div className="partner-modal-overlay">
          <div className="partner-modal">
            {!forgotStageSent ? (
              <>
                <h3 className="partner-modal-title">
                  Forgot Password
                </h3>
                <input
                  type="number"
                  placeholder="Enter your mobile number"
                  value={forgotMobile}
                  onChange={(e) => setForgotMobile(e.target.value)}
                  className="partner-modal-input"
                />
                <button
                  onClick={handleForgotSendOtp}
                  className="partner-modal-btn"
                >
                  Send OTP
                </button>
                <button
                  onClick={() => setShowForgotModal(false)}
                  className="partner-modal-cancel"
                >
                  Cancel
                </button>
              </>
            ) : !otpVerified ? (
              <>
                <h3 className="partner-modal-title">
                  Verify OTP
                </h3>
                <input
                  type="text"
                  placeholder="Enter OTP"
                  maxLength={6}
                  value={forgotOtp}
                  onChange={(e) => setForgotOtp(e.target.value)}
                  className="partner-modal-input partner-otp-input"
                />
                <button
                  onClick={handleVerifyForgotOtp}
                  className="partner-modal-btn"
                >
                  Verify OTP
                </button>
              </>
            ) : (
              <>
                <h3 className="partner-modal-title">
                  Reset Password
                </h3>
                <input
                  type="password"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="partner-modal-input"
                />
                <button
                  onClick={handleResetPassword}
                  className="partner-modal-btn"
                >
                  Update Password
                </button>
              </>
            )}
          </div>
        </div>
      )}

      <Footer />
    </>
  );
}

export default PartnerLoginPage;